/**
 * Download Helper
 *
 * Utilities for downloading files in the browser.
 * Provides functions to trigger file downloads and create ZIP archives.
 */

import JSZip from 'jszip';

/**
 * Trigger a file download in the browser
 *
 * @param content - File content (string or Blob)
 * @param filename - Name for the downloaded file
 * @param mimeType - MIME type (optional, will be inferred if content is Blob)
 */
export function downloadFile(
  content: string | Blob,
  filename: string,
  mimeType?: string
): void {
  const blob =
    content instanceof Blob
      ? content
      : new Blob([content], { type: mimeType || 'text/plain' });

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the blob URL after a short delay
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

/**
 * Create a ZIP archive and trigger download
 *
 * @param files - Array of files to include in the ZIP
 * @param zipFilename - Name for the ZIP file
 */
export async function downloadZip(
  files: Array<{
    name: string;
    content: string | Blob;
    type?: 'text' | 'blob';
  }>,
  zipFilename: string
): Promise<void> {
  const zip = new JSZip();

  // Add all files to the ZIP
  for (const file of files) {
    if (file.content instanceof Blob) {
      // Convert Blob to ArrayBuffer
      const arrayBuffer = await file.content.arrayBuffer();
      zip.file(file.name, arrayBuffer);
    } else {
      // Text content
      zip.file(file.name, file.content);
    }
  }

  // Generate ZIP blob
  const zipBlob = await zip.generateAsync({ type: 'blob' });

  // Trigger download
  downloadFile(zipBlob, zipFilename, 'application/zip');
}

/**
 * Download text content as a file
 *
 * @param content - Text content
 * @param filename - Name for the file
 */
export function downloadText(content: string, filename: string): void {
  downloadFile(content, filename, 'text/plain');
}

/**
 * Download JSON content as a file
 *
 * @param data - Data to serialize as JSON
 * @param filename - Name for the file
 */
export function downloadJSON(data: unknown, filename: string): void {
  const content = JSON.stringify(data, null, 2);
  downloadFile(content, filename, 'application/json');
}
